import { z } from "zod";

import { reportSchemaError } from "@/diagnostics/schema-error.ts";
import {
  DistrictCollectionSchema,
  indexDistricts,
  type DistrictCollection,
  type IndexedDistrict,
} from "./district.model.ts";

/**
 * The baked district polygons, returned both as raw GeoJSON (for the map
 * source) and pre-indexed with bounding boxes (for point-in-polygon filtering).
 * Regenerate the file with `aube run districts`.
 *
 * The file is a build artefact rather than a network payload, which is exactly
 * why it's parsed: a stale bake from an older schema is the failure that
 * actually happens, and unvalidated it surfaces much later as an undefined
 * `properties.id` somewhere in the picker.
 */
export async function fetchDistricts(): Promise<{
  collection: DistrictCollection;
  indexed: IndexedDistrict[];
}> {
  const res = await fetch("/districts.geojson");
  if (!res.ok) throw new Error(`/districts.geojson → HTTP ${res.status}`);

  const parsed = DistrictCollectionSchema.safeParse(await res.json());
  if (!parsed.success) {
    // Toast + console here; the throw lets the caller fall back to no districts.
    // It carries the prettified report rather than a generic "didn't validate",
    // so the caller's warn says which field was wrong without opening devtools.
    reportSchemaError("DistrictCollection", parsed.error);
    throw new Error(`/districts.geojson → ${z.prettifyError(parsed.error)}`);
  }

  return { collection: parsed.data, indexed: indexDistricts(parsed.data.features) };
}
